import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { SalaryAfterTaxReport } from '../../types/salaryAfterTax';
import {
  ArrowRight,
  CircleAlert,
  Loader2,
  Wallet,
  Building2,
  TrendingDown,
  CheckCircle2,
  Info,
  Euro,
  MapPin,
  Lightbulb,
  Zap,
  AlertTriangle,
  Compass,
  LayoutDashboard,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatMonthly = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatBudgetLabel = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const SalaryAfterTaxPage: React.FC = () => {
  const { salarySlug } = useParams<{ salarySlug: string }>();
  const [data, setData] = useState<SalaryAfterTaxReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const salaryValue = useMemo(() => Number((salarySlug || '').replace(/[^\d]/g, '')), [salarySlug]);

  useEffect(() => {
    if (!salaryValue) {
      setError(`Salary value is missing.`);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getSalaryAfterTaxReport?salary=${salaryValue}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError(`No report found for ${formatCurrency(salaryValue)}.`);
          return;
        }
        setData(result.data as SalaryAfterTaxReport);
      } catch (e) {
        setError('Unable to load report.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [salaryValue]);

  if (loading) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mb-4" />
        <p className="text-slate-500 font-bold animate-pulse">
          Calculating Ireland Take-Home Pay...
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="bg-white rounded-[2rem] p-10 shadow-xl border border-slate-100 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-4">Report Unavailable</h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Link
            to="/salary-after-tax"
            className="inline-block bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black hover:bg-emerald-700 transition-all"
          >
            Browse Salaries
          </Link>
        </div>
      </div>
    );
  }

  const taxPercentage = (data.calculations.total_deductions / data.salary_value) * 100;
  const takeHomePercentage = 100 - taxPercentage;
  const monthlyBudgetEntries = data.ai_enrichment?.life_in_ireland
    ? Object.entries(data.ai_enrichment.life_in_ireland.monthly_budget)
    : [];

  return (
    <div className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-[#f8fafc] min-h-screen font-sans antialiased text-slate-900">
      <Helmet>
        <title>
          {data.seo_content.h1 ||
            `${formatCurrency(data.salary_value)} After Tax Ireland (${data.tax_year})`}
        </title>
        <meta name="description" content={data.seo_content.meta_desc} />
        <script type="application/ld+json">{JSON.stringify(data.schema)}</script>
      </Helmet>

      {/* --- HERO SECTION --- */}
      <header className="bg-slate-900 text-white pt-12 pb-24 overflow-hidden relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] -mr-48 -mt-48" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] -ml-48 -mb-48" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row gap-12 items-start lg:items-center">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-black uppercase tracking-widest mb-6">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                {data.tax_year} RENTSMART.IE POTENTIAL SALARY AFTER TAX
              </div>
              <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 leading-[1.05]">
                {data.seo_content.h1 || `${formatCurrency(data.salary_value)} After Tax Ireland (${data.tax_year})`} <br />
              </h1>
              <p className="text-slate-400 text-lg md:text-xl max-w-2xl leading-relaxed mb-8 font-medium">
                {data.seo_content.meta_desc}
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                  Share Report:
                </span>
                <div className="flex gap-2">
                  <FacebookShareButton url={window.location.href}>
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton url={window.location.href} title={data.seo_content.meta_desc}>
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <WhatsappShareButton url={window.location.href}>
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton url={window.location.href}>
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[400px]">
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 shadow-2xl">
                <div className="space-y-6">
                  <div>
                    <div className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mb-1">
                      Monthly Take-Home
                    </div>
                    <div className="text-5xl font-black text-emerald-400">
                      {formatMonthly(data.calculations.net_monthly)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                    <div>
                      <div className="text-slate-500 text-[10px] font-black uppercase tracking-wider mb-1">
                        Rent Budget
                      </div>
                      <div className="text-xl font-bold">
                        {formatCurrency(data.affordability_metrics.recommended_rent)}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500 text-[10px] font-black uppercase tracking-wider mb-1">
                        Mortgage Limit
                      </div>
                      <div className="text-xl font-bold">
                        {formatCurrency(data.affordability_metrics.max_mortgage)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* --- DASHBOARD CONTENT --- */}
      <main className="max-w-7xl mx-auto px-6 -mt-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT: Financials & Breakdown */}
          <div className="lg:col-span-8 space-y-8">
            {/* Calculation Card */}
            <section className="bg-white rounded-[2.5rem] mt-20 shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-3 mb-10">
                  <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600">
                    <LayoutDashboard className="h-6 w-6" />
                  </div>
                  <h2 className="text-3xl font-black text-slate-900">Tax Breakdown</h2>
                </div>

                <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
                  <div className="relative flex items-center justify-center">
                    <svg className="w-56 h-56 transform -rotate-90">
                      <circle
                        cx="112"
                        cy="112"
                        r="100"
                        stroke="currentColor"
                        strokeWidth="16"
                        fill="transparent"
                        className="text-slate-100"
                      />
                      <circle
                        cx="112"
                        cy="112"
                        r="100"
                        stroke="currentColor"
                        strokeWidth="16"
                        fill="transparent"
                        strokeDasharray={628.3}
                        strokeDashoffset={628.3 - (628.3 * takeHomePercentage) / 100}
                        strokeLinecap="round"
                        className="text-emerald-500 transition-all duration-1000"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-4xl font-black text-slate-900">
                        {takeHomePercentage.toFixed(1)}%
                      </span>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Net Pay
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {[
                      {
                        label: 'Annual Gross',
                        val: data.salary_value,
                        icon: <Euro className="h-4 w-4" />,
                        color: 'bg-slate-900',
                      },
                      {
                        label: 'Income Tax (PAYE)',
                        val: data.calculations.paye,
                        icon: <TrendingDown className="h-4 w-4" />,
                        color: 'bg-rose-500',
                      },
                      {
                        label: 'USC',
                        val: data.calculations.usc,
                        icon: <TrendingDown className="h-4 w-4" />,
                        color: 'bg-rose-400',
                      },
                      {
                        label: 'PRSI',
                        val: data.calculations.prsi,
                        icon: <TrendingDown className="h-4 w-4" />,
                        color: 'bg-rose-300',
                      },
                    ].map((row, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded-lg text-white ${row.color}`}>
                            {row.icon}
                          </div>
                          <span className="font-bold text-slate-600 text-sm">{row.label}</span>
                        </div>
                        <span className="font-black text-slate-900">{formatCurrency(row.val)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-emerald-600 rounded-3xl p-8 text-white flex flex-col md:flex-row justify-between items-center gap-6">
                  <div>
                    <div className="text-emerald-200 text-xs font-black uppercase tracking-[0.2em] mb-1">
                      Final Net Annual
                    </div>
                    <div className="text-4xl font-black">
                      {formatCurrency(data.calculations.net_annual)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-emerald-200 text-xs font-black uppercase tracking-[0.2em] mb-1">
                      Total Tax Lost
                    </div>
                    <div className="text-2xl font-bold opacity-90">
                      {formatCurrency(data.calculations.total_deductions)}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* AI Enrichment Bento Grid */}
            <section className="space-y-8">
              <div className="flex items-center gap-3 px-4">
                <Zap className="h-6 w-6 text-amber-500 fill-amber-500" />
                <h2 className="text-3xl font-black text-slate-900">Lifestyle Insight</h2>
              </div>

              {data.ai_enrichment && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Market Perspective */}
                  <div className="md:col-span-2 bg-white rounded-[2.5rem] p-8 md:p-12 border border-slate-100 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 opacity-5 text-emerald-600">
                      <Lightbulb size={160} />
                    </div>
                    <h3 className="text-2xl font-black mb-6 flex items-center gap-2">
                      The Big Picture
                    </h3>
                    <p className="text-slate-600 text-xl leading-relaxed font-medium">
                      "{data.ai_enrichment.salary_story}"
                    </p>
                  </div>

                  {/* Renting vs Buying Lens */}
                  <div className="bg-emerald-50 rounded-[2.5rem] p-8 border border-emerald-100">
                    <h4 className="text-emerald-900 font-black mb-4 flex items-center gap-2">
                      <Wallet className="h-5 w-5" /> Renting Lens
                    </h4>
                    <p className="text-emerald-800/80 text-sm leading-relaxed font-medium">
                      {data.ai_enrichment.renting_context}
                    </p>
                  </div>

                  <div className="bg-sky-50 rounded-[2.5rem] p-8 border border-sky-100">
                    <h4 className="text-sky-900 font-black mb-4 flex items-center gap-2">
                      <Building2 className="h-5 w-5" /> Buying Lens
                    </h4>
                    <p className="text-sky-800/80 text-sm leading-relaxed font-medium">
                      {data.ai_enrichment.buying_context}
                    </p>
                  </div>

                  {/* Life in Ireland Detailed Dashboard */}
                  {data.ai_enrichment.life_in_ireland && (
                    <div className="md:col-span-2 bg-slate-900 rounded-[2.5rem] p-8 md:p-12 text-white">
                      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 mb-12">
                        <div className="xl:col-span-7">
                          <h3 className="text-2xl font-black mb-4">Daily Life & Cost of Living</h3>
                          <p className="text-slate-400 font-medium leading-relaxed mb-4">
                            {data.ai_enrichment.life_in_ireland.lifestyle_summary}
                          </p>
                          <div className="inline-block px-4 py-2 bg-white/10 rounded-xl text-emerald-400 font-bold text-sm">
                            Status: {data.ai_enrichment.life_in_ireland.cost_of_living_signal}
                          </div>
                        </div>

                        <div className="xl:col-span-5">
                          <div className="h-full bg-white/5 p-6 rounded-3xl border border-white/10">
                            <h5 className="text-xs font-black uppercase text-slate-500 tracking-widest mb-4">
                              Monthly Budget Guide
                            </h5>
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3">
                              {monthlyBudgetEntries.map(([key, val]) => (
                                <div
                                  key={key}
                                  className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 flex items-center justify-between gap-3"
                                >
                                  <span className="text-[11px] uppercase tracking-wider text-slate-400 font-black">
                                    {formatBudgetLabel(key)}
                                  </span>
                                  <span className="text-sm font-black text-white text-right">
                                    {val}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-emerald-400 font-black text-xs uppercase tracking-widest">
                            <CheckCircle2 className="h-4 w-4" /> Comforts
                          </div>
                          <ul className="space-y-2">
                            {data.ai_enrichment.life_in_ireland.comfort_points.map((p) => (
                              <li key={p} className="text-sm text-slate-300 font-medium flex gap-2">
                                <span className="text-emerald-500">•</span> {p}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-amber-400 font-black text-xs uppercase tracking-widest">
                            <AlertTriangle className="h-4 w-4" /> Stretch Points
                          </div>
                          <ul className="space-y-2">
                            {data.ai_enrichment.life_in_ireland.stretch_points.map((p) => (
                              <li key={p} className="text-sm text-slate-300 font-medium flex gap-2">
                                <span className="text-amber-500">•</span> {p}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-sky-400 font-black text-xs uppercase tracking-widest">
                            <Compass className="h-4 w-4" /> Practical Tips
                          </div>
                          <ul className="space-y-2">
                            {data.ai_enrichment.life_in_ireland.practical_tips.map((p) => (
                              <li key={p} className="text-sm text-slate-300 font-medium flex gap-2">
                                <span className="text-sky-500">•</span> {p}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* County Comparison */}
                  <div className="md:col-span-2">
                    <h3 className="text-xl font-black mb-6 px-4">Regional Comparison</h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      {data.ai_enrichment.county_comparison.map((c) => (
                        <div
                          key={c.county}
                          className="bg-white border border-slate-100 p-6 rounded-[2rem] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group"
                        >
                          <div className="flex items-center gap-2 mb-4">
                            <div className="p-2 bg-slate-50 rounded-xl group-hover:bg-emerald-50 transition-colors">
                              <MapPin className="h-4 w-4 text-emerald-600" />
                            </div>
                            <span className="font-black text-slate-900 tracking-tighter uppercase text-sm">
                              {c.county}
                            </span>
                          </div>
                          <p className="font-bold text-slate-800 mb-2 leading-tight">
                            {c.headline}
                          </p>
                          <p className="text-xs text-slate-500 leading-relaxed">{c.summary}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </section>

            <PseoEmailCapture
              pageType="salary-after-tax"
              pageSlug={data.slug}
              contextHeadline={`Get Salary & Property Updates For ${formatCurrency(data.salary_value)}`}
              contextReason={`Receive Irish property market updates tied to this salary band, including refreshed take-home analyses, rent pressure updates, and practical renter/buyer guidance.`}
            />

            {/* FAQ Section */}
            <section className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
              <h2 className="text-3xl font-black text-slate-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {data.seo_content.faq.map((item, idx) => (
                  <details
                    key={idx}
                    className="group bg-slate-50 rounded-2xl overflow-hidden transition-all"
                  >
                    <summary className="flex justify-between items-center p-6 cursor-pointer font-bold text-slate-800 list-none">
                      {item.q}
                      <span className="bg-white p-1 rounded-full group-open:rotate-180 transition-transform">
                        <ArrowRight className="h-4 w-4 rotate-90" />
                      </span>
                    </summary>
                    <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-white/50 pt-4">
                      {item.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Disclaimer */}
            <section className="bg-amber-50 rounded-[2.5rem] border border-amber-200 p-8 md:p-10 shadow-sm">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-black text-amber-900 mb-3">Important Disclaimer</h2>
                  <p className="text-amber-900/90 text-sm leading-relaxed mb-3">
                    The salary, tax, rent and mortgage figures on this page are general estimates
                    for informational purposes only. They do not account for your full personal
                    circumstances, tax credits, reliefs, benefits, liabilities or lender-specific
                    underwriting criteria.
                  </p>
                  <p className="text-amber-900/90 text-sm leading-relaxed mb-3">
                    RentSmart does not provide tax, financial, mortgage, legal or investment advice.
                    Nothing on this page is a guarantee of borrowing capacity, mortgage approval,
                    rental eligibility, property availability or future financial outcome.
                  </p>
                  <p className="text-amber-900/90 text-sm leading-relaxed">
                    You should verify all numbers independently and consult qualified professionals
                    (for example, a tax adviser, mortgage adviser or lender) before making financial
                    or property decisions.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* RIGHT: Sidebar sticky content */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="sticky top-8 space-y-6">
              {/* Action Cards */}
              <div className="bg-emerald-700 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl shadow-emerald-900/20">
                <Wallet className="h-12 w-12 mb-6 opacity-50 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-black mb-2">Ready to move?</h3>
                <p className="text-emerald-100/80 text-sm font-medium mb-8 leading-relaxed">
                  Filter Ireland's rental market by your net monthly budget of{' '}
                  {formatCurrency(data.affordability_metrics.recommended_rent)}.
                </p>
                <a
                  href={data.ctas.rent.path}
                  className="no-underline flex items-center justify-between bg-white text-emerald-700 py-4 px-6 rounded-2xl font-black hover:bg-emerald-50 transition-all"
                >
                  {data.ctas.rent.label} <ArrowRight className="h-5 w-5" />
                </a>
              </div>

              <div className="bg-sky-800 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl shadow-sky-900/20">
                <Building2 className="h-12 w-12 mb-6 opacity-50 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-black mb-2">Buy Property</h3>
                <p className="text-sky-100/80 text-sm font-medium mb-8 leading-relaxed">
                  View homes within your borrowing benchmark of{' '}
                  {formatCurrency(data.affordability_metrics.max_mortgage)}.
                </p>
                <a
                  href={data.ctas.buy.path}
                  className="no-underline flex items-center justify-between bg-white text-sky-800 py-4 px-6 rounded-2xl font-black hover:bg-sky-50 transition-all"
                >
                  {data.ctas.buy.label} <ArrowRight className="h-5 w-5" />
                </a>
              </div>

              {/* Steps Card */}
              {data.ai_enrichment?.action_plan && (
                <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
                  <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
                    <CheckCircle2 className="text-emerald-500 h-5 w-5" /> Action Plan
                  </h3>
                  <div className="space-y-6">
                    {data.ai_enrichment.action_plan.map((step, i) => (
                      <div key={i} className="flex gap-4">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-black text-xs text-slate-400">
                          0{i + 1}
                        </span>
                        <p className="text-sm font-bold text-slate-600 leading-tight pt-1">
                          {step}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Link
                to="/salary-after-tax"
                className="flex items-center justify-center gap-2 w-full py-5 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 font-black uppercase text-xs tracking-[0.2em] hover:border-emerald-500 hover:text-emerald-600 transition-all"
              >
                Try Another Salary <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>

        <section className="mt-12 bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">Share This Salary Report</h2>
              <p className="text-slate-600 text-sm">
                Share this page with someone comparing salaries, rent budgets, or mortgage options
                in Ireland.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <FacebookShareButton url={window.location.href}>
                <FacebookIcon size={36} round />
              </FacebookShareButton>
              <TwitterShareButton url={window.location.href} title={data.seo_content.meta_desc}>
                <TwitterIcon size={36} round />
              </TwitterShareButton>
              <WhatsappShareButton url={window.location.href}>
                <WhatsappIcon size={36} round />
              </WhatsappShareButton>
              <LinkedinShareButton url={window.location.href}>
                <LinkedinIcon size={36} round />
              </LinkedinShareButton>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SalaryAfterTaxPage;
